'use client';

import { useState } from 'react';
import { TabBar, typography } from '@bts-web/core-features/common';
import { css, cx, flex, stack } from '@bts-web/utils-style-engine';
import { intlAssetValueFormatting } from '@bts-web/utils-formatting';

type MetricKey = 'revenue' | 'ebitda' | 'net-income';

interface FinancialYearData {
  period: string;
  value: number;
}

export interface CompanyFinancialTranslations {
  revenue: string;
  ebitda: string;
  netIncome: string;
  columnPeriod: string;
  columnValue: string;
  columnChange: string;
  columnChangePct: string;
  attribution: string;
  noDataAvailable: string;
}

export interface AssetDetailsCompanyFinancialProps {
  locale: string;
  currency: string;
  revenue: FinancialYearData[] | null;
  ebitda: FinancialYearData[] | null;
  netIncome: FinancialYearData[] | null;
  translations: CompanyFinancialTranslations;
}

const positiveClass = css({ color: 'positive.text_primary' });

const negativeClass = css({ color: 'negative.text_primary' });

const neutralClass = css({ color: 'neutrals.text_secondary' });

function getChangeClass(change: number | null): string {
  if (change === null) return neutralClass;

  if (change > 0) return positiveClass;

  if (change < 0) return negativeClass;

  return neutralClass;
}

function getChangeIcon(change: number | null): string {
  if (change === null || change === 0) return '●';

  return change > 0 ? '▲' : '▼';
}

interface TableRow {
  period: string;
  formattedValue: string;
  yoyChange: number | null;
  formattedYoyChange: string | null;
  yoyChangePct: number | null;
  formattedYoyChangePct: string | null;
}

function computeRows(
  data: FinancialYearData[],
  locale: string,
  currency: string,
): TableRow[] {
  const sorted = [...data].sort((a, b) => b.period.localeCompare(a.period));

  const displayedRows = sorted.slice(0, 5);

  return displayedRows.map((row, idx) => {
    const prevRow = sorted[idx + 1];

    const formattedValue = intlAssetValueFormatting(row.value, {
      locale,
      currency,
      notation: 'compact',
      fractionDigits: 1,
    });

    if (!prevRow) {
      return {
        period: row.period,
        formattedValue,
        yoyChange: null,
        formattedYoyChange: null,
        yoyChangePct: null,
        formattedYoyChangePct: null,
      };
    }

    const yoyChange = row.value - prevRow.value;

    const yoyChangePct =
      prevRow.value !== 0 ? (yoyChange / prevRow.value) * 100 : null;

    const sign = yoyChange > 0 ? '+' : '';

    const formattedYoyChange =
      sign +
      intlAssetValueFormatting(yoyChange, {
        locale,
        currency,
        notation: 'compact',
        fractionDigits: 1,
      });

    const formattedYoyChangePct =
      yoyChangePct !== null
        ? intlAssetValueFormatting(Math.abs(yoyChangePct), {
            locale,
            fractionDigits: 2,
          }) + '%'
        : null;

    return {
      period: row.period,
      formattedValue,
      yoyChange,
      formattedYoyChange,
      yoyChangePct,
      formattedYoyChangePct,
    };
  });
}

const headerSeparator = {
  borderBottom: '1px solid',
  borderColor: 'neutrals.stroke_secondary',
};

const thClass = css({
  ...typography.captionMedium,
  ...headerSeparator,
  color: 'neutrals.text_secondary',
  textAlign: 'start',
  width: '20%',
  pb: 'small',
});

const thEndClass = css({
  ...typography.captionMedium,
  ...headerSeparator,
  color: 'neutrals.text_secondary',
  textAlign: 'end',
  width: '26.67%',
  pb: 'small',
});

const tdClass = css({
  ...typography.bodyMedium,
  textAlign: 'end',
  py: 'small',
});

const tdPeriodClass = css({
  ...typography.bodyMediumBold,
  textAlign: 'start',
  py: 'small',
});

const attributionClass = css({
  ...typography.captionSmall,
  color: 'neutrals.text_secondary',
});

const emptyStateClass = flex({
  justifyContent: 'center',
  alignItems: 'center',
  py: 'extra_large',
});

const emptyStateTextClass = css({
  ...typography.bodyMedium,
  color: 'neutrals.text_secondary',
});

export const AssetDetailsCompanyFinancials = ({
  locale,
  currency,
  revenue,
  ebitda,
  netIncome,
  translations,
}: AssetDetailsCompanyFinancialProps) => {
  const allTabs: {
    key: MetricKey;
    label: string;
    data: FinancialYearData[] | null;
  }[] = [
    { key: 'revenue', label: translations.revenue, data: revenue },
    { key: 'ebitda', label: translations.ebitda, data: ebitda },
    { key: 'net-income', label: translations.netIncome, data: netIncome },
  ];

  // null = hide tab entirely; [] = show tab with empty state
  const availableTabs = allTabs.filter((t) => t.data !== null);

  const [selectedMetric, setSelectedMetric] = useState<MetricKey>(
    availableTabs[0]?.key ?? 'revenue',
  );

  if (availableTabs.length === 0) return null;

  const activeData = allTabs.find((t) => t.key === selectedMetric)?.data ?? [];

  const rows = computeRows(activeData, locale, currency);

  const tabItems = availableTabs.map((t) => ({ label: t.label, value: t.key }));

  return (
    <div className={stack({ gap: 'medium' })}>
      <TabBar
        tabItems={tabItems}
        value={selectedMetric}
        onValueChange={(v) => setSelectedMetric(v as MetricKey)}
      />

      {rows.length === 0 ? (
        <div className={emptyStateClass}>
          <p className={emptyStateTextClass}>{translations.noDataAvailable}</p>
        </div>
      ) : (
        <>
          <table className={css({ width: '100%', borderCollapse: 'collapse' })}>
            <thead>
              <tr>
                <th className={thClass}>{translations.columnPeriod}</th>
                <th className={thEndClass}>{translations.columnValue}</th>
                <th className={thEndClass}>{translations.columnChange}</th>
                <th className={thEndClass}>{translations.columnChangePct}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const changeClass = getChangeClass(row.yoyChange);

                return (
                  <tr key={row.period}>
                    <td className={tdPeriodClass}>{row.period}</td>
                    <td className={tdClass}>{row.formattedValue}</td>
                    <td className={cx(tdClass, changeClass)}>
                      {row.formattedYoyChange ?? '—'}
                    </td>
                    <td className={cx(tdClass, changeClass)}>
                      {row.formattedYoyChangePct !== null ? (
                        <>
                          {getChangeIcon(row.yoyChange)}{' '}
                          {row.formattedYoyChangePct}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className={attributionClass}>{translations.attribution}</p>
        </>
      )}
    </div>
  );
};
